// "El sistema recuerde inputs previos que haya escrito el doctor [en el
// encabezado] y ofrezca el autocompletado con esas memorias, para todos los
// formularios de Laboratorio y Estudios/Documentos" — pedido explícito del
// usuario 2026-09-04. Antes solo `unicodigo`/`establecimiento` se
// recordaban (un único valor silencioso, sin sugerencias); ahora los 5
// campos de texto libre del encabezado (`institucion`/`unicodigo`/
// `establecimiento`/`numeroArchivo`/`hoja`) guardan hasta `MAX_RECORDADOS`
// valores distintos cada uno — mismo patrón de `<datalist>` +
// `recetas/telefonosPrescriptorRecientes.ts`. Conveniencia de
// navegador/dispositivo (localStorage), no dato clínico que necesite
// sincronizarse entre sesiones ni vivir en el expediente.
//
// Clave por `practitionerId` (pedido explícito del usuario, ronda 2): igual
// que `telefonosPrescriptorRecientes.ts` — un equipo compartido no debe
// mezclar lo que tipeó un practicante con lo de otro.
import type { CamposEncabezadoMsp } from './EncabezadoMspCampos';

const PREFIJO_CLAVE = 'logiacare.formulario.encabezadoMsp.recientes.';
const MAX_RECORDADOS = 5;

type CampoTextoLibre = keyof CamposEncabezadoMsp;

function claveDe(practitionerId: string, campo: CampoTextoLibre): string {
  return `${PREFIJO_CLAVE}${campo}:${practitionerId}`;
}

function recientes(practitionerId: string, campo: CampoTextoLibre): string[] {
  try {
    const crudo = localStorage.getItem(claveDe(practitionerId, campo));
    if (!crudo) return [];
    const lista = JSON.parse(crudo);
    return Array.isArray(lista) ? lista.filter((v) => typeof v === 'string') : [];
  } catch {
    return [];
  }
}

/** Lo manda al frente de la lista (más reciente primero), sin duplicados, máximo `MAX_RECORDADOS`. */
function recordar(practitionerId: string, campo: CampoTextoLibre, valor: string): void {
  const limpio = valor.trim();
  if (!limpio) return;
  const actuales = recientes(practitionerId, campo).filter((v) => v !== limpio);
  const actualizada = [limpio, ...actuales].slice(0, MAX_RECORDADOS);
  try {
    localStorage.setItem(claveDe(practitionerId, campo), JSON.stringify(actualizada));
  } catch {
    // localStorage lleno o bloqueado (modo privado) — es solo una
    // conveniencia de autocompletado, no vale la pena romper el flujo del
    // formulario por esto.
  }
}

/** Últimos valores distintos que se tipearon en este campo, más reciente primero — para poblar el `<datalist>` de cada Input del encabezado. */
export function recientesDe(practitionerId: string, campo: CampoTextoLibre): string[] {
  return recientes(practitionerId, campo);
}

export function recordarCampoEncabezado(practitionerId: string, campo: CampoTextoLibre, valor: string): void {
  recordar(practitionerId, campo, valor);
}

/** El último `unicodigo`/`establecimiento` recordado — no cambian de un formulario MSP a otro dentro del mismo consultorio, así que arrancar el campo ya precargado (además de ofrecerlo en el `<datalist>`) ahorra más que solo sugerirlo. */
export function ultimoUnicodigoYEstablecimiento(practitionerId: string): { unicodigo: string; establecimiento: string } {
  return {
    unicodigo: recientes(practitionerId, 'unicodigo')[0] ?? '',
    establecimiento: recientes(practitionerId, 'establecimiento')[0] ?? '',
  };
}
